import {gsap} from "gsap/all";
import $ from "jquery";
import {BaseGameMars} from "../base/BaseGameMars";

export class Rover {
  constructor(app, uniqueId, containerElement) {
    this.app = app;
    this.$container = $(containerElement);
    this.uniqueId = uniqueId;

    // Game data
    this.content = this.app?.data?.content || {};
    this.gameData = this.content.game || {};
    this.globalProperties = this.gameData.properties || {};

    // Get default color
    const defaultColorObj = (this.gameData.colors || []).find(c => c.default === true);
    this.defaultColorId = defaultColorObj ? String(defaultColorObj.id) : "6";

    // Rover default configuration
    this.configKeys = [
      'cockpit',
      'wheels',
      'data_collection',
      'utilities',
      'sample_collection'
    ];
    this.defaultConfig = {};
    this.configKeys.forEach(key => {
      this.defaultConfig[key] = {id: "", color: this.defaultColorId};
    });

    // Rover current config, clone from default on init
    this.config = structuredClone(this.defaultConfig);

    // Rover current stats
    this.currentStats = {};

    // Init
    if (this.$container.length) {
      this.init();
    }
  }

  init() {
    // Apply default color to all options
    this.configKeys.forEach(key => {
      this.updateColor(key, this.defaultColorId);
    });
  }

  /**
   * Update the slide view and configuration for this specific rover instance.
   * @param {string} optionType ('cockpit' or category like 'wheels')
   * @param {null|string} optionId (optional direct ID override)
   * @param {string} direction ('left' or 'right', defaults to 'left')
   * @param {number} duration (animation duration in seconds, defaults to 0.4)
   */
  updateVisual(optionType, optionId = null, direction = 'left', duration = 0.40) {
    let $slides, $activeCockpit, configKey;

    // Determine whether we are switching the main cockpit view or an option slide inside it
    if (optionType === 'cockpit') {
      $slides = this.$container.find('> .cockpit-view');
      configKey = 'cockpit';

      // RESET Options when cockpit change
      Object.keys(this.defaultConfig).forEach(key => {
        if (key !== 'cockpit') this.setConfig(key, "id", this.defaultConfig[key].id);
      });
      const $subSlides = this.$container.find('.cockpit-view .option-group .option-slide');
      $subSlides.removeClass('is-active');
      gsap.set($subSlides, {autoAlpha: 0, x: 0, overwrite: true});
    } else {
      // Get current cockpit
      $activeCockpit = this.$container.find('.cockpit-view.is-active');

      // If the placeholder is active, abort
      if (!$activeCockpit.length || $activeCockpit.is('.rover-placeholder')) return;

      // Get option group
      $slides = $activeCockpit.find(`.option-group[data-category="${optionType}"] .option-slide`);
      configKey = optionType;
    }

    // No slides are found, abort
    if (!$slides.length) return;

    // Determine the next slide to display
    // Exclude cockpit placeholder from reachable slides
    let $navigableSlides = $slides;
    if (optionType === 'cockpit') {
      // Can't manually reach empty cockpit
      $navigableSlides = $slides.not('.rover-placeholder');
    } else if (optionType === 'wheels') {
      // Can't manually reach empty wheels
      $navigableSlides = $slides.not('[data-id=""]');
    }

    const $current = $slides.filter('.is-active');
    let $next;
    if (optionId !== null) {
      // Explicit selection: target a specific slide by its data-id
      $next = $slides.filter(`[data-id="${optionId}"]`);
    } else if (!$current.length || !$navigableSlides.filter($current).length) {
      // Initial fallback: default to the first navigable slide if nothing is active yet
      $next = $navigableSlides.first();
    } else {
      // Move forward or backward through slides with infinite looping
      let index = $navigableSlides.index($current);
      index = direction === 'right' ? index + 1 : index - 1;

      // Wrap around boundaries for infinite looping
      if (index >= $navigableSlides.length) index = 0;
      if (index < 0) index = $navigableSlides.length - 1;
      $next = $navigableSlides.eq(index);
    }

    // The option wasn't found, abort
    if (!$next.length) return;

    // Update config
    this.setConfig(configKey, "id", $next.attr('data-id'));

    if ($current.length) $current.removeClass('is-active');
    $next.addClass('is-active');

    // GSAP Transition
    // Old element leaves the scene toward the opposite side
    const xOffset = direction === 'left' ? 45 : -45;
    if ($current.length) {
      gsap.to($current, {
        autoAlpha: 0.0, x: duration > 0 ? -xOffset : 0,
        duration: duration, ease: 'power1.out', overwrite: true
      });
    }
    gsap.fromTo($next, {
        autoAlpha: 0.0, x: duration > 0 ? xOffset : 0
      }, {
        autoAlpha: 1.0, x: 0, duration: duration,
        ease: 'power1.out', overwrite: true
      }
    );
  }

  /**
   * Update the color for a specific option type
   * @param {string} optionType
   * @param {string} colorId (e.g., '1', '6', etc.)
   */
  updateColor(optionType, colorId) {
    if (!optionType || !colorId) return;

    // Convert colorId to actual color using json data
    const colors = this.gameData.colors || [];
    const colorObj = colors.find(c => String(c.id) === String(colorId));
    const resolvedHex = colorObj ? colorObj.hex : '#FFFFFF';

    // Save the color ID in the config (not the hex)
    this.setConfig(optionType, "color", colorId);

    // Find target elements globally across the container
    let $targets;
    if (optionType === 'cockpit') {
      $targets = this.$container.find('.cockpit-view');
    } else {
      $targets = this.$container.find(`.option-group[data-category="${optionType}"] .option-slide`);
    }

    // Apply the hex or transparent value to your CSS variable
    if ($targets.length) {
      // Use a different css variable to avoid conflict between cockpit and options
      let cssVariable = optionType === "cockpit" ? "--cockpit-color" : "--option-color";
      if (resolvedHex.toUpperCase().startsWith("#FFF")) {
        $targets.css(cssVariable, "transparent");
      } else {
        $targets.css(cssVariable, resolvedHex);
        // This is an option where we avoid the middle "too black" state with "overlay"
        // BaseGameMars.killTimeline(this.colorTimeline);
        // this.colorTimeline = gsap.timeline();
        // this.colorTimeline.add(() => $targets.css(cssVariable, "transparent"));
        // this.colorTimeline.add(() => $targets.css(cssVariable, resolvedHex), "+=0.3");
      }
    }
  }

  /**
   * Used to update the rover configuration
   * @param optionType
   * @param key
   * @param value
   */
  setConfig(optionType, key = "id", value) {
    this.config[optionType][key] = value;
  }

  /**
   * Return a code that represent the configuration of rover
   * Ex. (A1)(B2)(A3)(C6)(00)
   * Format inside parentheses: [optionId][colorId] (single character each, e.g., A1, or 00 for empty).
   * @return string
   */
  getConfigCode() {
    let code = '';
    this.configKeys.forEach(key => {
      const option = this.config[key] || {};
      const id = option.id ? option.id : '0';
      const color = option.color ? option.color : '0';
      code += `${id}${color}`;
    });
    return code;
  }

  /**
   * Calculate and store the stats based on the current config
   * @return {Object}
   */
  getConfigStats() {
    const newStats = {};

    // Reset to default values
    Object.entries(this.globalProperties).forEach(([key, property]) => {
      newStats[key] = property.default_value ?? "-";
    });

    const roversData = this.gameData.rover || {};
    const activeCockpitId = this.config.cockpit?.id;
    const cockpitData = roversData[activeCockpitId];

    // If no cockpit, return empty stats
    if (!activeCockpitId || !cockpitData) {
      this.currentStats = newStats;
      return this.currentStats;
    }

    // Add cockpit properties
    if (cockpitData.properties) {
      Object.assign(newStats, cockpitData.properties);
    }

    // Add/merge option properties
    this.configKeys.forEach(optionType => {
      if (optionType === "cockpit") return;

      const optionId = this.config[optionType]?.id;
      const optionProperties = cockpitData[optionType]?.[optionId]?.properties;
      if (!optionProperties) return;

      // Merge option properties: add numbers together or override non-numbers (like text/booleans)
      Object.entries(optionProperties).forEach(([key, value]) => {
        if (typeof newStats[key] !== "number" || typeof value !== "number") newStats[key] = 0;
        // newStats[key] += value;
        // Use this weird conversion to fixed decimal string to prevent JavaScript binary floating-point math rounding issue
        newStats[key] = parseFloat((newStats[key] + value).toFixed(2));
      });
    });

    // Save stats and return copy of them
    this.currentStats = newStats;
    return this.currentStats;
  }

  /**
   * Apply a rover configuration code.
   * Format: [optionId][colorId] repeated for each config key.
   * Example: A1B2C300D6E1
   *
   * @param {string} code
   * @param {number} duration Animation duration (default 0)
   * @returns {boolean} True if applied successfully.
   */
  applyConfig(code, duration = 0) {
    if (typeof code !== "string") {
      console.error("The rover configuration must be a string.");
      return false;
    }

    const expectedLength = this.configKeys.length * 2;
    if (code.length !== expectedLength) {
      console.error(
        `The rover configuration must be exactly ${expectedLength} characters.`
      );
      return false;
    }

    const segments = code.match(/.{2}/g) || [];
    segments.forEach((segment, index) => {
      const optionType = this.configKeys[index];
      if (!optionType) return;

      // Handle empty option (00)
      if (segment === "00") {
        this.updateVisual(optionType, "", "left", duration);
        return;
      }

      const optionId = segment.charAt(0);
      const colorId = segment.charAt(1);

      this.updateVisual(optionType, optionId, "left", duration);
      if (colorId && colorId !== "0") {
        this.updateColor(optionType, colorId);
      }
    });

    this.getConfigStats();

    return true;
  }

  reset() {
    // Reset rover by forcing an empty cockpit
    this.updateVisual("cockpit", "", "", 0);

    // Reset colors
    this.configKeys.forEach(key => {
      this.updateColor(key, this.defaultColorId);
    });

    // Replace config with default config
    this.config = structuredClone(this.defaultConfig);

    // Recalculate/reset currentStats to defaults
    this.getConfigStats();
  }
}
